/**
 * US Shipping Estimator Bridge
 *
 * Provides a ZIP-only shipping estimator for US visitors. Derives the state
 * from the ZIP prefix and writes it into the existing (hidden) international
 * form, then dispatches a synthetic input event so the existing
 * ShippingEstimator class in theme.js fetches rates unchanged.
 *
 * Activates on any .shipping-estimator[data-us-default="true"] element.
 */
(function () {
  'use strict';

  /**
   * ZIP 3-digit prefix → 2-letter state/territory code.
   * Stored as ranges (inclusive) to keep the table human-readable.
   * Ranges are based on USPS ZCTA assignments.
   */
  var ZIP_RANGES = [
    { start: 5,   end: 5,   code: 'NY' },   // Holtsville
    { start: 6,   end: 7,   code: 'PR' },
    { start: 8,   end: 8,   code: 'VI' },
    { start: 9,   end: 9,   code: 'PR' },
    { start: 10,  end: 27,  code: 'MA' },
    { start: 28,  end: 29,  code: 'RI' },
    { start: 30,  end: 38,  code: 'NH' },
    { start: 39,  end: 49,  code: 'ME' },
    { start: 50,  end: 54,  code: 'VT' },
    { start: 56,  end: 59,  code: 'VT' },
    { start: 60,  end: 69,  code: 'CT' },
    { start: 70,  end: 89,  code: 'NJ' },
    { start: 90,  end: 98,  code: 'AE' },   // military Europe
    { start: 100, end: 149, code: 'NY' },
    { start: 150, end: 196, code: 'PA' },
    { start: 197, end: 199, code: 'DE' },
    { start: 200, end: 205, code: 'DC' },
    { start: 206, end: 219, code: 'MD' },
    { start: 220, end: 246, code: 'VA' },
    { start: 247, end: 268, code: 'WV' },
    { start: 270, end: 289, code: 'NC' },
    { start: 290, end: 299, code: 'SC' },
    { start: 300, end: 319, code: 'GA' },
    { start: 320, end: 339, code: 'FL' },
    { start: 340, end: 340, code: 'AA' },   // military Americas
    { start: 341, end: 349, code: 'FL' },
    { start: 350, end: 369, code: 'AL' },
    { start: 370, end: 385, code: 'TN' },
    { start: 386, end: 397, code: 'MS' },
    { start: 398, end: 399, code: 'GA' },
    { start: 400, end: 427, code: 'KY' },
    { start: 430, end: 459, code: 'OH' },
    { start: 460, end: 479, code: 'IN' },
    { start: 480, end: 499, code: 'MI' },
    { start: 500, end: 528, code: 'IA' },
    { start: 530, end: 549, code: 'WI' },
    { start: 550, end: 567, code: 'MN' },
    { start: 570, end: 577, code: 'SD' },
    { start: 580, end: 588, code: 'ND' },
    { start: 590, end: 599, code: 'MT' },
    { start: 600, end: 629, code: 'IL' },
    { start: 630, end: 658, code: 'MO' },
    { start: 660, end: 679, code: 'KS' },
    { start: 680, end: 693, code: 'NE' },
    { start: 700, end: 714, code: 'LA' },
    { start: 716, end: 729, code: 'AR' },
    { start: 730, end: 749, code: 'OK' },
    { start: 750, end: 799, code: 'TX' },
    { start: 800, end: 816, code: 'CO' },
    { start: 820, end: 831, code: 'WY' },
    { start: 832, end: 838, code: 'ID' },
    { start: 840, end: 847, code: 'UT' },
    { start: 850, end: 865, code: 'AZ' },
    { start: 870, end: 884, code: 'NM' },
    { start: 889, end: 898, code: 'NV' },
    { start: 900, end: 961, code: 'CA' },
    { start: 962, end: 966, code: 'AP' },   // military Pacific
    { start: 967, end: 968, code: 'HI' },
    { start: 969, end: 969, code: 'GU' },
    { start: 970, end: 979, code: 'OR' },
    { start: 980, end: 994, code: 'WA' },
    { start: 995, end: 999, code: 'AK' }
  ];

  /**
   * 2-letter code → full province name as used by Shopify's
   * country_option_tags data-provinces array. Names must match exactly —
   * CountrySelector selects by option text, not value (see theme.js:1038).
   */
  var STATE_CODE_TO_NAME = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas',
    CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware',
    DC: 'District of Columbia', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii',
    ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
    KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine',
    MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska',
    NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico',
    NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
    OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island',
    SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas',
    UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
    WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
    PR: 'Puerto Rico', VI: 'Virgin Islands', GU: 'Guam',
    AS: 'American Samoa', MP: 'Northern Mariana Islands',
    AA: 'Armed Forces Americas', AE: 'Armed Forces Europe',
    AP: 'Armed Forces Pacific'
  };

  function lookupStateByZip(zip) {
    if (!/^\d{5}$/.test(zip)) return null;
    var prefix = parseInt(zip.substring(0, 3), 10);
    for (var i = 0; i < ZIP_RANGES.length; i++) {
      var r = ZIP_RANGES[i];
      if (prefix >= r.start && prefix <= r.end) {
        return r.code;
      }
    }
    return null;
  }

  var LOCALSTORAGE_ZIP_KEY = 'nlp_shipping_zip';
  var LOCALSTORAGE_DENIED_KEY = 'nlp_shipping_geo_denied';
  var BIGDATACLOUD_URL = 'https://api-bdc.io/data/reverse-geocode-client';

  function safeLocalStorageGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }

  function safeLocalStorageSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* noop */ }
  }

  function safeLocalStorageRemove(key) {
    try { window.localStorage.removeItem(key); } catch (e) { /* noop */ }
  }

  function USShippingBridge(root) {
    this.root = root;
    this.usView = root.querySelector('.shipping-estimator__view--us');
    this.intlView = root.querySelector('.shipping-estimator__view--intl');
    this.usZipInput = root.querySelector('.shipping-estimator__us-zip');
    this.confirmation = root.querySelector('.shipping-estimator__confirmation');
    this.invalidHint = root.querySelector('.shipping-estimator__invalid-hint');
    this.switchLink = root.querySelector('.shipping-estimator__switch-link');
    this.geoButton = root.querySelector('.shipping-estimator__geo-btn');
    this.geoButtonText = root.querySelector('.shipping-estimator__geo-btn-text');
    this.results = root.querySelector('.shipping-estimator__results');
    this.intlCountry = root.querySelector('[name="country"]');
    this.intlProvince = root.querySelector('[name="province"]');
    this.intlZip = root.querySelector('.shipping-estimator__view--intl [name="zip"]');

    if (!this.usView || !this.usZipInput || !this.intlZip) {
      return;
    }

    this._debounceTimer = null;
    this._geoDefaultText = this.geoButtonText ? this.geoButtonText.textContent : 'Use my location';
    this._geoInFlight = false;

    // Hide geo button if browser lacks geolocation support
    if (this.geoButton && !('geolocation' in navigator)) {
      this.geoButton.style.display = 'none';
    }

    this._attachListeners();
    this._restoreSavedZip();
  }

  USShippingBridge.prototype._attachListeners = function () {
    var self = this;

    this.usZipInput.addEventListener('input', function () {
      clearTimeout(self._debounceTimer);
      self._debounceTimer = setTimeout(function () {
        self._onZipInput();
      }, 300);
    });

    if (this.switchLink) {
      this.switchLink.addEventListener('click', function (e) {
        e.preventDefault();
        self._toggleView();
      });
    }

    if (this.geoButton) {
      this.geoButton.addEventListener('click', function (e) {
        e.preventDefault();
        self._onGeoButtonClick();
      });
    }
  };

  USShippingBridge.prototype._restoreSavedZip = function () {
    var savedZip = safeLocalStorageGet(LOCALSTORAGE_ZIP_KEY);
    if (!savedZip || !/^\d{5}$/.test(savedZip)) {
      return;
    }
    this.usZipInput.value = savedZip;
    // Fire synchronously (skip debounce) — we're restoring a known-good value
    this._onZipInput();
  };

  USShippingBridge.prototype._onGeoButtonClick = function () {
    if (this._geoInFlight) return;
    if (!('geolocation' in navigator)) return;

    var self = this;
    this._setGeoButtonLoading(true);
    this._hide(this.confirmation);
    this._hide(this.invalidHint);

    navigator.geolocation.getCurrentPosition(
      function (position) { self._handleGeoSuccess(position); },
      function (err) { self._handleGeoError(err); },
      { timeout: 8000, maximumAge: 300000, enableHighAccuracy: false }
    );
  };

  USShippingBridge.prototype._handleGeoSuccess = function (position) {
    var self = this;
    var lat = position.coords.latitude;
    var lon = position.coords.longitude;
    var url = BIGDATACLOUD_URL +
      '?latitude=' + encodeURIComponent(lat) +
      '&longitude=' + encodeURIComponent(lon) +
      '&localityLanguage=en';

    fetch(url, { method: 'GET', credentials: 'omit' })
      .then(function (response) {
        if (!response.ok) throw new Error('geocode_http_' + response.status);
        return response.json();
      })
      .then(function (data) {
        if (data.countryCode !== 'US') {
          self._showInvalidHintText('Location detected outside the US. Please enter a ZIP manually.');
          self._setGeoButtonLoading(false);
          return;
        }
        var postcode = (data.postcode || '').toString();
        var match = postcode.match(/^\d{5}/);
        if (!match) {
          self._showInvalidHintText('Could not determine ZIP from your location.');
          self._setGeoButtonLoading(false);
          return;
        }
        var zip = match[0];
        self.usZipInput.value = zip;
        self._onZipInput();
        self._setGeoButtonLoading(false);
      })
      ['catch'](function () {
        self._showInvalidHintText('Could not verify your location. Please enter a ZIP manually.');
        self._setGeoButtonLoading(false);
      });
  };

  USShippingBridge.prototype._handleGeoError = function (err) {
    if (err && err.code === 1) {
      // PERMISSION_DENIED — remember so we don't nag implicitly (button still visible)
      safeLocalStorageSet(LOCALSTORAGE_DENIED_KEY, '1');
      this._showInvalidHintText('Location access denied. Enter your ZIP code manually.');
    } else if (err && err.code === 3) {
      this._showInvalidHintText('Location request timed out. Please try again or enter a ZIP manually.');
    } else {
      this._showInvalidHintText('Could not determine your location. Please try again or enter a ZIP manually.');
    }
    this._setGeoButtonLoading(false);
  };

  USShippingBridge.prototype._setGeoButtonLoading = function (isLoading) {
    if (!this.geoButton) return;
    this._geoInFlight = !!isLoading;
    this.geoButton.disabled = !!isLoading;
    if (this.geoButtonText) {
      this.geoButtonText.textContent = isLoading ? 'Locating…' : this._geoDefaultText;
    }
    this.geoButton.classList.toggle('is-loading', !!isLoading);
  };

  USShippingBridge.prototype._saveZip = function (zip) {
    safeLocalStorageSet(LOCALSTORAGE_ZIP_KEY, zip);
  };

  USShippingBridge.prototype._clearZip = function () {
    safeLocalStorageRemove(LOCALSTORAGE_ZIP_KEY);
  };

  USShippingBridge.prototype._onZipInput = function () {
    var zip = (this.usZipInput.value || '').trim();

    // Clear previous messages/results when input changes meaningfully
    this._hide(this.confirmation);
    this._hide(this.invalidHint);

    if (zip.length < 5) {
      // Silent for partial input
      return;
    }

    var stateCode = lookupStateByZip(zip);
    if (!stateCode) {
      this._showInvalidHint();
      // Invalid zip — clear any stale saved value so next page load doesn't loop
      this._clearZip();
      return;
    }

    var stateName = STATE_CODE_TO_NAME[stateCode] || '';
    this._syncToIntlForm(zip, stateName);
    this._showConfirmation(stateCode, zip);
    this._saveZip(zip);
  };

  USShippingBridge.prototype._syncToIntlForm = function (zip, stateName) {
    // Ensure country is United States on the intl form
    if (this.intlCountry) {
      var matched = false;
      for (var i = 0; i < this.intlCountry.options.length; i++) {
        if (this.intlCountry.options[i].text === 'United States') {
          if (this.intlCountry.selectedIndex !== i) {
            this.intlCountry.selectedIndex = i;
            this.intlCountry.dispatchEvent(new Event('change', { bubbles: true }));
          }
          matched = true;
          break;
        }
      }
      if (!matched) {
        // Log but continue — Shopify may still return rates with just zip
        if (window.console && console.warn) {
          console.warn('[us-shipping] United States not found in country selector');
        }
      }
    }

    // Set province by matching option text
    if (this.intlProvince && stateName) {
      var provinceFound = false;
      for (var j = 0; j < this.intlProvince.options.length; j++) {
        if (this.intlProvince.options[j].text === stateName) {
          this.intlProvince.selectedIndex = j;
          provinceFound = true;
          break;
        }
      }
      if (!provinceFound && window.console && console.warn) {
        console.warn('[us-shipping] Province not found: ' + stateName);
      }
    }

    // Set zip and dispatch input event so ShippingEstimator picks it up
    this.intlZip.value = zip;
    this.intlZip.dispatchEvent(new Event('input', { bubbles: true }));
  };

  USShippingBridge.prototype._showConfirmation = function (stateCode, zip) {
    if (!this.confirmation) return;
    var template = this.confirmation.getAttribute('data-template') ||
      'Shipping to {{ state }} · {{ zip }}';
    this.confirmation.textContent = template
      .replace('{{ state }}', stateCode)
      .replace('{{ zip }}', zip);
    this._show(this.confirmation);
  };

  USShippingBridge.prototype._showInvalidHint = function () {
    if (!this.invalidHint) return;
    // Reset to default text in case a geo-specific message was set previously
    this.invalidHint.textContent = this.invalidHint.getAttribute('data-default-text') ||
      this.invalidHint.textContent || 'Enter a valid US ZIP code';
    this._show(this.invalidHint);
    // Clear any previous results to avoid stale display
    if (this.results) {
      this.results.innerHTML = '';
      this.results.style.display = 'none';
    }
  };

  USShippingBridge.prototype._showInvalidHintText = function (text) {
    if (!this.invalidHint) return;
    this.invalidHint.textContent = text;
    this._show(this.invalidHint);
    this._hide(this.confirmation);
    if (this.results) {
      this.results.innerHTML = '';
      this.results.style.display = 'none';
    }
  };

  USShippingBridge.prototype._toggleView = function () {
    var usActive = this.usView.classList.contains('is-active');
    if (usActive) {
      this.usView.classList.remove('is-active');
      this.intlView.classList.add('is-active');
      this.switchLink.textContent = this.switchLink.getAttribute('data-label-to-us') ||
        'Ship to US with ZIP only';
    } else {
      this.intlView.classList.remove('is-active');
      this.usView.classList.add('is-active');
      this.switchLink.textContent = this.switchLink.getAttribute('data-label-to-intl') ||
        'Ship to another country?';
    }

    // Clear results and messages on toggle
    if (this.results) {
      this.results.innerHTML = '';
      this.results.style.display = 'none';
    }
    this._hide(this.confirmation);
    this._hide(this.invalidHint);
  };

  USShippingBridge.prototype._show = function (el) {
    if (el) el.hidden = false;
  };

  USShippingBridge.prototype._hide = function (el) {
    if (el) el.hidden = true;
  };

  function init() {
    var estimators = document.querySelectorAll('.shipping-estimator[data-us-default]');
    for (var i = 0; i < estimators.length; i++) {
      new USShippingBridge(estimators[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
